<template>
  <div :class="`${onRowClick ? 'pointer' : ''}`" :style="`margin-bottom:${isMobile ? '100px' : ''}`">
    <ve-table
      id="loading-container"
      row-key-field-name="id"
      :columns="currentColumns"
      :table-data="rows"
      :checkbox-option="checkboxOption"
      style="width: 100%"
      :border-around="true"
      :border-x="true"
      :border-y="false"
      :sort-option="sortOption"
      :event-custom-option="eventCustomOption"
      :rowStyleOption="{ clickHighlight }"
      :expand-option="expand ? expandOption : isMobile ? expandOptionMobile : {}"
      :cellSelectionOption="{ enable: false }"
      :cellStyleOption="{ bodyCellClass }"
    />
    <div v-if="!rows || !rows.length" class="no-data">
      {{ noTableDataTitle }}
    </div>
    <div v-if="additionalHtml" v-html="additionalHtml"></div>
    <div v-if="pagination && filter" class="pagination-block">
      <ve-pagination
        :total="filter.totalCount"
        :page-size="filter.limit"
        :page-index="filter.page"
        :page-size-option="[5, 50, 200, 500, 1000]"
        @on-page-number-change="pageNumberChange"
        @on-page-size-change="pageSizeChange"
      />
    </div>
  </div>
</template>

<script>
import { NO_TABLE_DATA_TITLE } from '../../../../../../../constants';

import deDE from 'vue-easytable/libs/locale/lang/de-DE';
import enUS from 'vue-easytable/libs/locale/lang/en-US';

export default {
  title: 'Table',
  props: {
    update: {
      type: Function,
      default: () => {},
    },
    pagination: {
      type: Boolean,
      default: true,
    },
    clickHighlight: {
      type: Boolean,
      default: true,
    },
    setLoadingInstance: {
      type: Function,
      default: () => {},
    },
    loadingInstance: {
      type: Object,
      default: null,
    },
    columns: {
      type: Array,
      default: () => [],
    },
    rows: {
      type: Array,
      default: () => [],
    },
    selectedList: {
      type: Array,
      default: () => [],
    },
    setSelectedList: {
      type: Function,
      default: () => {},
    },
    filter: {
      type: Object,
      default: () => {},
    },
    updateFilter: {
      type: Function,
      default: () => {},
    },
    onRowClick: {
      type: Function,
      default: null,
    },
    expand: {
      type: Boolean,
      default: false,
    },
    expandOption: {
      type: Object,
      default: () => {},
    },
    tableName: {
      type: String,
      default: '',
      required: true, // need for sorting and must be unique
    },
    additionalHtml: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      html: '<span style="color: red">This should be red.</span>',
      currentColumns: this.columns,
      expandOptionMobile: {
        expandable: ({ row, column, rowIndex }) => {
          return true;
        },
        render: ({ row, column, rowIndex }, h) => {
          const columns = this.columns.slice(this.maxElements).filter((c) => c.title && c.field !== 'actions');
          return (
            <div class="expend-wrapper">
              {columns.map((c) => {
                return (
                  <div class="expend-box">
                    <div class="expend-title">{c.title}</div>
                    <div class="expend-value">{row[c.field]}</div>
                  </div>
                );
              })}
            </div>
          );
        },
      },
      eventCustomOption: {
        bodyRowEvents: ({ row, rowIndex }) => {
          return {
            click: (e) => {
              if (this.onRowClick) {
                this.onRowClick(row, rowIndex);
              }
            },
          };
        },
      },
      noTableDataTitle: this.$translate(NO_TABLE_DATA_TITLE),
      sortOption: {
        sortChange: (params) => {
          const updateTableSorting = (currentFilter) => {
            const tableSorting = JSON.parse(localStorage.getItem('tableSorting')) || {};
            const sortingData = { sortBy: currentFilter.sortBy, sortOrder: currentFilter.sortOrder };
            localStorage.setItem('tableSorting', JSON.stringify({ ...tableSorting, [this.tableName]: sortingData }));
          };
          let allEmpty = true;
          Object.keys(params).some((key) => {
            if (params[key]) {
              allEmpty = false;
              const f = {
                sortBy: key,
                sortOrder: params[key],
              };
              updateTableSorting(f);
              this.updateFilter(f);
              this.update();
              return true;
            }
          });
          if (allEmpty) {
            const f = {
              sortBy: '',
              sortOrder: '',
            };
            updateTableSorting(f);
            this.updateFilter(f);
            this.update();
          }
        },
      },
      checkboxOption: {
        selectedRowKeys: this.selectedList,
        selectedRowChange: ({ selectedRowKeys }) => {
          const s = this.rows.filter((i) => selectedRowKeys.includes(i.id));
          this.setSelectedList(s);
        },
        selectedAllChange: ({ selectedRowKeys }) => {
          const s = this.rows.filter((i) => selectedRowKeys.includes(i.id));
          this.setSelectedList(s);
        },
      },
      isMobile: false,
      maxElements: 5,
    };
  },
  watch: {
    selectedList(newVal) {
      this.checkboxOption.selectedRowKeys = newVal.map((i) => i.id);
    },
    filter(newFilter) {
      const currentSortedColumn = this.currentColumns.find(
        ({ field, sortBy }) => field === newFilter.sortBy && sortBy === newFilter.sortOrder,
      );
      if (!(currentSortedColumn + 1)) {
        this.currentColumns = this.currentColumns.map((column) => {
          const result = { ...column };
          if (column.sortBy !== undefined) {
            result.sortBy = this.filter.sortBy === column.field ? this.filter.sortOrder : '';
          }
          return result;
        });
      }
    },
  },
  async mounted() {
    const l = this.$veLoading({
      target: document.querySelector('#loading-container'),
      name: 'wave',
    });
    const limit = localStorage.getItem('table-limit');

    let f;
    if (limit) {
      f = {
        limit: Number(limit) || 50,
      };
    } else {
      f = {
        limit: 50,
      };
    }
    this.updateFilter(f);

    this.setLoadingInstance(l);
    await this.update();
  },
  async created() {
    window.addEventListener('resize', this.resizeFunction);
    this.resizeFunction();
    if (this.lng === 'de') {
      this.$veLocale.use(deDE);
    } else {
      this.$veLocale.use(enUS);
    }

    const tableSorting = JSON.parse(localStorage.getItem('tableSorting')) || {};
    const currentTableSorting = tableSorting[this.tableName];
    if (currentTableSorting && currentTableSorting.sortBy && currentTableSorting.sortOrder) {
      this.updateFilter(currentTableSorting);
    }
  },
  destroyed() {
    if (this.loadingInstance) {
      this.loadingInstance.destroy();
    }
    window.removeEventListener('resize', this.resizeFunction);
  },
  methods: {
    resizeFunction() {
      const renderMaxelements = (width) => {
        if (width >= 900) {
          return 5;
        }
        if (width > 600) {
          return 4;
        }
        if (width > 400) {
          return 3;
        }
        if (width <= 400) {
          return 2;
        }
      };

      const width = window.innerWidth;
      const isMobile = width <= 1100;

      const maxElements = renderMaxelements(width);
      const informFields = ['status'];
      const isInform = this.columns.slice(0, maxElements).some((i) => informFields.includes(i.field));

      this.maxElements = maxElements + (isInform ? 1 : 0);

      this.isMobile = isMobile && maxElements < this.columns.length - 1;
    },
    pageNumberChange(i) {
      const f = {
        page: i,
      };
      this.updateFilter(f);
      this.setSelectedList([]);
      this.update();
    },
    pageSizeChange(s) {
      const limitNumber = Number(s);
      localStorage.setItem('table-limit', limitNumber);
      const f = {
        page: 1,
        limit: limitNumber,
      };
      this.updateFilter(f);
      this.setSelectedList([]);
      this.update();
    },
    bodyCellClass({ column }) {
      if (column.field === 'status') {
        return 'p-0 position-relative';
      }
    },
    startLoading() {
      this.loadingInstance.show();
    },
    stopLoading() {
      this.loadingInstance.close();
    },
  },
};
</script>

<style scoped lang="scss">
.shadow {
  box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;
}

::v-deep .ve-table.ve-table-border-around {
  border-radius: 4px;
}

::v-deep .ve-table .ve-table-container .ve-table-content-wrapper table.ve-table-content tbody.ve-table-body .ve-table-expand-tr .ve-table-expand-td-content,
::v-deep .ve-table .ve-table-container .ve-table-content-wrapper table.ve-table-content thead.ve-table-header.ve-table-fixed-header tr th {
  z-index: 4;
}

::v-deep .ve-loading.ve-loading-overlay {
  z-index: 1000 !important;
}

::v-deep .ve-pagination-goto {
  display: flex !important;
  align-items: center !important;
}
::v-deep .ve-pagination-total {
  display: flex !important;
  align-items: center !important;
  margin: 0 10px !important;
}
::v-deep .ve-pagination-select {
  display: flex;
  align-items: center;
}

::v-deep .ve-pagination {
  height: 40px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-evenly;
  align-items: center;
}

::v-deep .ve-table-header-th {
  white-space: nowrap;
}
::v-deep .ve-table-body {
  ::v-deep th {
    color: rgb(112, 112, 112) !important;
    background-color: rgb(239, 239, 239) !important;
    border: none !important;
    min-height: 50px;
  }
  ::v-deep td {
    color: rgb(112, 112, 112) !important;
    cursor: pointer;
  }
}

::v-deep .ve-table-cell-selection {
  border: none !important;
}

::v-deep .ve-table-sort {
  top: -4.5px;
}

::v-deep .ve-select-selected-span {
  font-weight: 400;
}

::v-deep .ve-checkbox {
  margin-bottom: 0;
}

.pagination-block {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.no-data {
  color: #7586a5;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 30px;
}

.pointer {
  ::v-deep tbody {
    cursor: pointer;
  }
}

::v-deep .expend-wrapper {
  display: flex;
  flex-wrap: wrap;
}

::v-deep .expend-box {
  flex: 1;
  margin: 5px 10px;
}

::v-deep .expend-title {
  font-weight: 500;
  color: #000000d9;
  font-size: 14px;
  margin-bottom: 10px;
}


::v-deep .ve-table-header-th .ve-dropdown {
  width: 20px;

  & i {
    color: #bfbfbf;
  }
}
</style>
